import {
  TopNewsSummaryFragment,
  useTopNewsQuery,
} from "@graphql/generated/graphql";
import classNames from "@lib/classNames";
import { errorToastPopup } from "@lib/errorToastPopup";
import { Trans } from "@lingui/macro";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useMemo } from "react";

interface ISideBarTopNewsProps {
  topNews?: TopNewsSummaryFragment[];
  indentItems?: boolean;
  applyWidthConstraints?: boolean;
}

const SideBarTopNews: React.FC<ISideBarTopNewsProps> = ({
  topNews,
  indentItems = true,
  applyWidthConstraints = true,
}) => {
  const {
    query: { id },
    pathname,
    locale,
  } = useRouter();

  const shouldFetch = !topNews || topNews.length === 0;

  const { data, loading, refetch, error } = useTopNewsQuery({
    skip: !shouldFetch,
    variables: { languageCode: locale },
  });

  const topNewsList = useMemo(() => {
    const baseTopNewsList = shouldFetch ? data?.topNews ?? [] : topNews ?? [];

    if (pathname === "/news/[id]") {
      return baseTopNewsList.filter((news) => news.id != id);
    } else {
      return baseTopNewsList;
    }
  }, [data?.topNews, id, pathname, shouldFetch, topNews]);

  useEffect(() => {
    if (shouldFetch) {
      refetch({ languageCode: locale });
    }
  }, [shouldFetch, locale, refetch]);

  useEffect(() => {
    if (error) {
      errorToastPopup(error);
    }
  }, [error]);

  return (
    <div
      className={classNames(
        applyWidthConstraints ? "lg:max-w-[390px]" : "mx-auto w-full"
      )}
    >
      <p className=" font-poppins text-lg font-bold text-text-darkest">
        <Trans>What's Hot This Hour</Trans>
      </p>
      {loading && (
        <p className="py-4 text-center font-sen text-text-lightest">
          Loading...
        </p>
      )}
      {topNewsList && !error && (
        <div className="flex flex-col divide-y divide-off-white">
          {topNewsList.map(({ id, title, newsPublisher }, index) => {
            return (
              <div key={id} className="py-2">
                <div className="flex flex-row items-center gap-x-4">
                  <div
                    className={classNames(
                      "text-md flex h-[40px] w-[40px] items-center justify-center rounded-full bg-light-background px-4 font-sen text-text-darkest lg:ml-0",
                      indentItems ? "ml-4" : null
                    )}
                  >
                    {index + 1}
                  </div>

                  <div className="min-w-0 flex-1">
                    <Link href={`/publishers/${newsPublisher.slug}`} passHref>
                      <p className="font-sen text-sm text-text-lightest transition-colors duration-300 hover:cursor-pointer hover:text-text-dark">
                        {newsPublisher.name}
                      </p>
                    </Link>
                    <Link href={`/news/${id}`} passHref>
                      <p className="font-poppins font-bold text-text-darkest transition-colors duration-300 hover:cursor-pointer hover:text-text-dark">
                        {title}
                      </p>
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SideBarTopNews;
